import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"

import { toZonedTime } from "date-fns-tz"
import { getStaleDate } from "../utils/dates"
import {
    getRefId,
    isMissionComplete,
    getObjectId,
    formatError,
    useDebouncedEffect,
    getMissionStatus,
    getMissionStatusCls,
} from "../utils/utils"
import { missionWarnings } from "../utils/mission"
import { makeHash, makeRefHash } from "../utils/utils"
import Loadable from "react-loadable"
import Badge from "@material-ui/core/Badge"
import { addDbItem, getDbItem, deleteIndexedDB } from "../utils/idb-util"
import Tip from "../comp/Tip"
import Bubble from "../comp/Bubble"
import { getLeaveDate } from "../comp/PeopleLeaveUtils"
import cx from "classnames"
import Tabs from "../comp/Tabs"
import uniqBy from "lodash/uniqBy"
import apiMission from "../services/mission/mission.api"
import { subMinutes, isAfter, subDays, format } from "date-fns"
import { addParam } from "../comp/History"
import "./Board.scss"
import { doRolesRequireApproval } from "../utils/gt-allocation"
import { decompress, decrypt, compress, encrypt } from "../utils/cache-utils"
import { getEncryptionKey } from "../utils/key-utils"
import {
    PiClockDuotone,
    PiClockCounterClockwiseDuotone,
    PiCreditCardDuotone,
    PiAirplaneDuotone,
    PiCalendarCheckDuotone,
    PiStackDuotone,
    PiAlignLeftDuotone,
    PiRocketDuotone,
    PiFileTextDuotone,
} from "react-icons/pi"
import apiTimesheet from "../services/timesheet/timesheet.api"
import apiExpenses from "../services/expense/expense.api"
import apiPeopleLeave from "../services/peopleLeave/peopleLeave.api"
import flatMap from "lodash/flatMap"
import { updateStateTimesheets } from "../utils/timesheets"
import { updateStateLeaves } from "../utils/leaves"
import { updateStateExpenses } from "../utils/expenses"
import { removeParam } from "../comp/History"
import groupBy from "lodash/groupBy"

import "ag-grid-enterprise"
import { AgGridReact } from "ag-grid-react"
import "ag-grid-community/styles/ag-grid.css"
import "ag-grid-community/styles/ag-theme-alpine.css"
import "../scss/DnaAgGrid.scss"

const PmTimesheets = Loadable({
    loader: () => import("../comp/PmTimesheets"),
    loading: () => <span />,
})

const VisionBoard = Loadable({
    loader: () => import("../comp/VisionBoard"),
    loading: () => <span />,
})

const Scheduling = Loadable({
    loader: () => import("../comp/Scheduling"),
    loading: () => <span />,
})

const PmLeaves = Loadable({
    loader: () => import("../comp/PmLeaves"),
    loading: () => <span />,
})

const Docs = Loadable({
    loader: () => import("../comp/Docs"),
    loading: () => <span />,
})

const ResourceTimeline = Loadable({
    loader: () => import("../comp/ResourceTimeline"),
    loading: () => <span />,
})

const CostTracker = Loadable({
    loader: () => import("../comp/CostTracker"),
    loading: () => <span />,
})

const ProjectPlanner = Loadable({
    loader: () => import("../comp/ProjectPlanner"),
    loading: () => <span />,
})

const ExpenseTracker = Loadable({ loader: () => import("../comp/ExpenseTracker"), loading: () => <span /> })

const Board = ({ app, stateChange }) => {
    const [tabs, setTabs] = useState([])
    const [tabIndex, setTabIndex] = useState(0)
    const [pmMissions, setPmMissions] = useState([])
    const [licensedPmMissions, setLicensedPmMissions] = useState([])
    const [nonPmMissions, setNonPmMissions] = useState([])
    const [completeMissions, setCompleteMissions] = useState([])
    const [hasOeraFlow, setHasOeraFlow] = useState()
    const [updateKey, setUpdateKey] = useState([])
    const [timesheets, setTimesheets] = useState([])
    const [expenses, setExpenses] = useState([])
    const [leaves, setLeaves] = useState([])
    const [warningData, setWarningData] = useState([])
    const timesheetsRef = useRef([])
    const expensesRef = useRef([])
    const leavesRef = useRef([])
    const [leavesBadge, setLeavesBadge] = useState(0)
    const changeTab = (indx) => {
        removeParam("board-tab")

        const tabClicked = tabs[indx]

        localStorage.setItem("x-board-tab", tabClicked?.id)
        setTabIndex(indx)
    }

    useEffect(() => {
        lastStateChange.current = null
        ranRef.current = null
    }, [app.state.mission, app.state.org])

    useEffect(() => {
        const q = new URLSearchParams(window.location.search)
        if (q.has("board-tab")) {
            const index = tabs.findIndex((t) => t.id === q.get("board-tab"))
            setTabIndex(index === -1 ? 0 : index)
        } else {
            const savedTabId = localStorage.getItem("x-board-tab")

            const savedTabIndex = savedTabId ? tabs.findIndex((t) => t.id === savedTabId) : 0

            if (+savedTabIndex >= 0) {
                setTabIndex(+savedTabIndex || 0)
            }
        }
    }, [licensedPmMissions.length, tabs])

    const lastStateChange = useRef()
    const ranRef = useRef()

    useEffect(() => {
        let xl = leaves?.filter((l) => {
            return (
                isAfter(getLeaveDate(l.dates[0].to), subDays(new Date(), 3)) &&
                l.status === "pending_approval" &&
                !l.reviews?.find((r) => r.by === app.state.person._id)
            )
        }).length

        setLeavesBadge(xl || 0)
    }, [leaves])
    useEffect(() => {
        const passAlong = {
            app,
            stateChange,
            context: "pm",
            getPmLeaves,
            asyncTimesheets,
            asyncExpenses,
            asyncLeaves,
            timesheets,
            leaves,
        }

        let myTabs = [
            {
                title: () => (
                    <>
                        <PiRocketDuotone />
                        {licensedPmMissions.length ? "Missions" : "Launchpad"}
                    </>
                ),
                id: "vision-board",
                comp: VisionBoard,
                notComponent: true,
                params: {
                    ...passAlong,
                    pmMissions: licensedPmMissions,
                    myMissions: nonPmMissions,
                    timesheets,
                },
            },
        ]

        if (licensedPmMissions.length) {
            myTabs = [
                ...myTabs,
                {
                    title: () => (
                        <>
                            <PiAlignLeftDuotone />
                            Planner{" "}
                        </>
                    ),
                    comp: ProjectPlanner,
                    notComponent: true,
                    id: "pm-Planner",
                    params: {
                        missions: licensedPmMissions.filter((m) => {
                            return m.projectType === "mx-gantt" && m.kickedOff === true && !m.isModel && !m.isTemplate
                        }),
                        app,
                        timesheets,
                        leaves,
                        stateChange,
                    },
                },
                {
                    title: "Cost tracker",
                    comp: CostTracker,
                    notComponent: true,
                    id: "cost-tracker",
                    params: {
                        ...passAlong,
                        missions: licensedPmMissions,
                        givenTimesheets: timesheets,
                        context: "multi",
                    },
                },
                {
                    title: () => (
                        <Badge
                            badgeContent={timesheets?.filter((ts) => ts.status === "submitted")?.length}
                            color="primary"
                        >
                            <PiClockDuotone />
                            Timesheets
                        </Badge>
                    ),
                    comp: PmTimesheets,
                    notComponent: true,
                    id: "timesheets",
                    params: {
                        ...passAlong,
                        missions: licensedPmMissions,
                        timesheets,
                        timesheetsRef,
                        updateTimesheetsInState,
                        updateKey,
                        context: "multi",
                    },
                },
                {
                    title: () => (
                        <Badge
                            badgeContent={expenses?.filter((ex) => ex.status === "submitted")?.length}
                            color="primary"
                        >
                            <PiCreditCardDuotone />
                            Expenses
                        </Badge>
                    ),
                    comp: ExpenseTracker,
                    id: "expenses",
                    notComponent: true,
                    params: {
                        ...passAlong,
                        missions: licensedPmMissions,
                        context: "pm",
                        givenExpenses: expenses,
                        updateExpensesInState,
                    },
                },
                {
                    title: () => (
                        <Badge badgeContent={leavesBadge} color="primary">
                            <PiAirplaneDuotone />
                            Leaves
                        </Badge>
                    ),
                    comp: PmLeaves,
                    id: "leaves",
                    notComponent: true,
                    params: {
                        ...passAlong,
                        missions: licensedPmMissions,
                        leaves,
                        updateLeavesInState,
                        asyncLeaves,
                    },
                },
            ]

            if (hasOeraFlow) {
                myTabs.push({
                    title: "Request OERA Resources",
                    comp: ResourceTimeline,
                    notComponent: true,
                    id: "gt-request",
                    className: "gt-tab",
                    params: {
                        ...passAlong,
                        fromBoard: true,
                        app: app,
                        isAllocationMode: true,
                        missions: licensedPmMissions,
                        missionChange: stateChange,
                        leaveRequests: leaves,
                        context: "pm",
                    },
                })
                myTabs.push({
                    title: "Assign Approved Resources",
                    comp: ResourceTimeline,
                    notComponent: true,
                    id: "gt-assign",
                    className: "gt-tab",
                    params: {
                        ...passAlong,
                        fromBoard: true,
                        app: app,
                        isAssignmentMode: true,
                        missions: licensedPmMissions,
                        missionChange: stateChange,
                        leaveRequests: leaves,
                        context: "pm",
                    },
                })
            }

            myTabs.push({
                title: () => <Tip title="Off-task project scheduling">Scheduling</Tip>,
                comp: Scheduling,
                notComponent: true,
                id: "scheduling",
                params: {
                    ...passAlong,
                    missions: licensedPmMissions,
                    missionChange: stateChange,
                    leaveRequests: leaves,
                    isBoard: true,
                },
            })

            myTabs.push({
                title: () => (
                    <>
                        <PiFileTextDuotone />
                        Mission docs
                    </>
                ),
                comp: Docs,
                notComponent: true,
                params: {
                    ...passAlong,
                    app: app,
                    missions: licensedPmMissions,
                    missionChange: stateChange,
                    leaveRequests: leaves,
                },
            })
        }

        myTabs.push({
            title: () => (
                <Tip title="Load completed, older or abandoned projects not already in view">
                    <PiClockCounterClockwiseDuotone />
                    The vault
                </Tip>
            ),
            id: "older-board",
            comp: TheVault,
            notComponent: true,
            params: {
                ...passAlong,
            },
        })
        setTabs(myTabs)
    }, [stateChange?.timestamp, updateKey])

    useEffect(() => {
        let pmM = []
        let teamM = []
        let completeM = []
        let hasGT = false
        app.state.missions.forEach((m, i) => {
            const meInM = m?.people?.find((p) => getRefId(p) === app.state.person._id)

            if (!meInM || m.loadedFromOrg) {
                return
            }

            if (m.isTemplate || meInM.permission > 3) return
            if (isMissionComplete(m)) {
                completeM.push(m)
                return
            }

            if (meInM) {
                if (m.org && (meInM.isProjectManager || meInM.permission === 2)) {
                    if (m.isLicensedActive) {
                        pmM.push(m)
                    }

                    try {
                        const orgData = app.state.orgs.find((o) => getObjectId(o) === getObjectId(m.org))
                        if (doRolesRequireApproval(m, orgData)) {
                            hasGT = true
                        }
                    } catch (e) {}
                } else {
                    teamM.push(m)
                }
            }
        })
        setHasOeraFlow(hasGT)
        setNonPmMissions(teamM)
        setPmMissions(pmM.map((m) => ({ ...m, isPm: true })))
        setCompleteMissions(completeM)
        setLicensedPmMissions(pmM)

        setUpdateKey(new Date().getTime())
    }, [app.state.missions.length, stateChange.timestamp])

    useEffect(() => {
        const badIds = ["pm-Planner", "cost-tracker", "timesheets", "expenses", "scheduling", "gt-request", "gt-assign"]

        if (!tabs?.length) return

        if (badIds.includes(tabs[tabIndex]?.id)) {
            try {
                setTimeout(() => {
                    app.hideAppAdminTools()
                }, 500)
            } catch (e) {
            } finally {
            }
        } else {
            app.showAppAdminTools()
        }
    }, [tabIndex, tabs])

    useDebouncedEffect(
        () => {
            let warningTimeout, timesheetsTimeout, expensesTimeout, leavesTimeout

            if (licensedPmMissions.length) {
                const wd = missionWarnings({ missions: licensedPmMissions, app })
                setWarningData(wd)
                timesheetsTimeout = setTimeout(() => {
                    try {
                        asyncTimesheets()
                    } catch (e) {}
                }, 400) // Add appropriate delay

                expensesTimeout = setTimeout(() => {
                    asyncExpenses()
                }, 1000) // Add appropriate delay

                asyncLeaves()
            }

            return () => {
                clearTimeout(warningTimeout)
                clearTimeout(timesheetsTimeout)
                clearTimeout(expensesTimeout)
                clearTimeout(leavesTimeout)
            }
        },
        [licensedPmMissions.length],
        300
    )

    const asyncTimesheets = async (force) => {
        let ts = []
        try {
            ts = await getPmTimesheets({ licensedPmMissions, app })

            if (ts?.length) updateTimesheetsInState({ how: "set", data: ts })
        } catch (err) {
            app.silentFail("ts-sss" + err.message)
            app.unBlock()
        }
    }
    const asyncExpenses = async () => {
        const expen = await getPmExpenses({
            app,
            pmMissions: licensedPmMissions.filter((m) => {
                const org = app.state.orgs?.find((o) => o._id === getObjectId(m.org))

                if (org.invoicingDisabled) {
                    return false
                }

                return true
            }),
        })
        updateExpensesInState({ how: "set", data: expen?.filter((ex) => ex.status !== "draft") })
    }
    const asyncLeaves = async (force) => {
        if (!app.state.board) {
            return
        }

        const leav = await getPmLeaves({ app, pmMissions: licensedPmMissions })

        updateLeavesInState({ how: "set", data: leav?.filter((lv) => lv.status !== "draft") || [] })
    }
    const updateTimesheetsInState = ({ how, data }) => {
        const newSheets = updateStateTimesheets({
            timesheets: timesheetsRef.current,
            how,
            data,
        })

        setTimesheets(newSheets)
        timesheetsRef.current = newSheets
        setUpdateKey(new Date().getTime())
    }
    const updateExpensesInState = ({ how, data }) => {
        const newExpenses = updateStateExpenses({
            expenses: expensesRef.current,
            how,
            data,
        })

        setExpenses(newExpenses)
        expensesRef.current = newExpenses
        setUpdateKey(new Date().getTime())
    }
    const updateLeavesInState = ({ how, data }) => {
        const newLeaves = updateStateLeaves({
            leaves: leavesRef.current,
            how,
            data,
        })

        setLeaves(newLeaves)
        leavesRef.current = newLeaves
        setUpdateKey(new Date().getTime())
    }

    const MyTabs = Tabs({
        data: tabs,
        activeTabIndex: tabIndex,
        returnArray: true,
        bodyClassName: "x-board-panels",
        onChange: (i, data) => {
            changeTab(i, data)
        },
    })

    return (
        <>
            <div className="x-pm-board">
                {MyTabs[0]}
                {MyTabs[1]}
            </div>
            {Boolean(!licensedPmMissions?.length) && !app.state.createMission && (
                <Bubble
                    app={app}
                    tag="x-pm-space-nav"
                    x={24}
                    y={70}
                    top={0}
                    delay={0}
                    position="fixed"
                    arrow={"top"}
                    msg={
                        <>
                            <p>
                                <b>The PM Work Space</b> <PiStackDuotone color="gold" />
                            </p>
                            <p>
                                <p>
                                    As a project or program manager, you'll see more tabs and features for managing
                                    budgets, people, timesheets, and more. Otherwise, it's a great visual launchpad for
                                    your projects.
                                </p>
                            </p>
                        </>
                    }
                />
            )}
        </>
    )
}
const getPmTimesheets = async ({ licensedPmMissions, app }) => {
    let timesheets = []

    if (!licensedPmMissions?.length) {
        return []
    }

    let lastUpdate

    let dbData

    try {
        dbData = await getDbItem("pmTimesheets", "store", app.state.person._id)
    } catch (e) {
        app.silentFail("ddd")
    }

    if (dbData?.lastFetch) {
        lastUpdate = dbData.lastFetch
    }

    if (dbData?.timesheets?.length) {
        timesheets = dbData?.timesheets
    }

    try {
        const newRecords = await apiTimesheet.getProjectManagerTimesheets({
            updatedAt: lastUpdate,
        })
        if (Array.isArray(newRecords)) timesheets = uniqBy([...newRecords, ...timesheets], "_id")
    } catch (e) {
        app.silentFail("wkjerhiejrld-ttsss" + e.message)
        timesheets = []
    }

    if (!timesheets?.map) {
        timesheets = []
        app.silentFail("wkjerhiejrld-ttsss-2")
    }

    const groupByMissionSheets = groupBy(
        timesheets?.map((ts) => ({ ...ts, missionId: getObjectId(ts.mission) })),
        "missionId"
    )

    const missionHash = makeHash(licensedPmMissions)

    let clearedTimesheets = []

    Object.keys(groupByMissionSheets).forEach((key, i) => {
        const foundMission = missionHash[key]

        if (!foundMission) {
            return
        }

        let missionSheets = groupByMissionSheets[key]

        if (foundMission.noTimesheets) {
            missionSheets = missionSheets.filter((ts) => Boolean(ts.status === "approved" || ts.status === "submitted"))
        }

        clearedTimesheets = uniqBy([...clearedTimesheets, ...missionSheets], "_id")
    })
    try {
        await addDbItem("pmTimesheets", "store", {
            id: app.state.person._id,
            timesheets: timesheets,
            lastFetch: subMinutes(new Date(), 30).toISOString(),
        })
    } catch (e) {
        deleteIndexedDB("pmTimesheets")
        app.silentFail("snkskjokwesldkmwms")
    } finally {
    }

    return clearedTimesheets
}

//

export const getPmLeaves = async ({ app, pmMissions, force }) => {
    const orgs = uniqBy(
        pmMissions.map((m) => {
            return app.state.orgs.find((o) => o._id === getObjectId(m.org))
        }),
        "_id"
    )

    if (!orgs.length) {
        return []
    }

    const allLeavesByOrg = []

    for (const org of orgs) {
        const meInOrg = org.people.find((p) => getRefId(p) === app.state.person._id)

        const myKey = await getEncryptionKey({ orgData: org, meInOrg, app })

        const orgKey = meInOrg._id

        let cachedOrgLeaves = []

        if (myKey) {
            const encryptedLeavesObj = await getDbItem("pmLeaves", "store", orgKey)
            if (encryptedLeavesObj?.leaves) {
                const decryptedLeaves = await decrypt(myKey, encryptedLeavesObj.leaves)
                const decompressedLeaves = decompress(decryptedLeaves)

                cachedOrgLeaves.push(...decompressedLeaves)
                allLeavesByOrg.push(...decompressedLeaves)
            }

            const newLeavesRecords = await apiPeopleLeave.getProjectManagerLeaveRequestList({
                lastUpdate: encryptedLeavesObj?.lastFetch,
            })

            allLeavesByOrg.push(...newLeavesRecords)

            if (newLeavesRecords.length) {
                const compressedLeaves = compress(uniqBy([...newLeavesRecords, ...cachedOrgLeaves], "_id"))
                const encryptedLeaves = await encrypt(myKey, compressedLeaves)

                await addDbItem("pmLeaves", "store", {
                    id: app.state.person._id,
                    leaves: encryptedLeaves,
                    lastFetch: subMinutes(new Date(), 5).toISOString(),
                })
            }
        }
    }

    const peopleHash = makeRefHash(flatMap(pmMissions, "people").filter((p) => p.permission < 3))

    let clearedLeaves = []

    uniqBy(allLeavesByOrg, "_id").forEach((leave, i) => {
        if (peopleHash[leave.person]) {
            clearedLeaves.push(leave)
        }
    })

    return clearedLeaves
}
const getPmExpenses = async ({ app, pmMissions }) => {
    let results = []

    try {
        const mHash = makeHash(pmMissions)
        results = await apiExpenses.getProjectManagerExpenses()

        results = results.filter((r) => mHash[r.mission])
    } catch (e) {
        app.silentFail("eoiwueExppps:" + formatError(e))
    }

    return results || []
}

const TheVault = ({ app }) => {
    const gridRef = useRef()
    const fetchedCompleted = useRef()
    const [completedMissions, setCompletedMissions] = useState([])

    useDebouncedEffect(() => {
        app.block()
        fetchedCompleted.current = true

        apiMission.getMissionListTree({ isActive: false, staleDate: getStaleDate() }).then((res) => {
            let mss = []

            res.forEach((m, i) => {
                if (!m.isTemplate && !app.state.missions.find((o) => o._id === m._id && m.loadedFromOrg)) {
                    mss.push(app.cleanBadData(m))
                }
            })

            setCompletedMissions(mss)

            app.unBlock()
        })
    }, [])

    const columnDefs = [
        { field: "projectCode", maxWidth: 110, headerName: "code" },
        { field: "title", headerName: "Project  name", flex: 1 },
        {
            headerName: "Type",
            maxWidth: 120,
            valueFormatter: (params) => {
                return params.data.isModel ? "Model project" : params.data.isProposal ? "Proposal" : "Project"
            },
        },
        {
            field: "status",
            maxWidth: 110,
            headerName: "Status",
            cellRenderer: (params) => {
                return (
                    <div className={cx("dna-pill-box", getMissionStatusCls(params.data))}>
                        {getMissionStatus(params.data, true)}
                    </div>
                )
            },
        },
        {
            field: "planEndDate",
            maxWidth: 130,
            headerName: "Start Date",
            valueFormatter: (params) => {
                return format(toZonedTime(params.data.planStartDate), "MMM d, yyyy")
            },
        },
        {
            field: "planEndDate",
            maxWidth: 130,
            headerName: "End Date",
            valueFormatter: (params) => {
                return format(toZonedTime(params.data.missionStop || params.data.planEndDate), "MMM d, yyyy")
            },
            sortable: true,
            sort: "desc",
        },
    ]

    const defaultColDef = useMemo(() => {
        return {
            suppressMovable: true,
            menuTabs: [],
        }
    }, [])

    const getRowId = useCallback((params) => {
        return params.data?._id
    }, [])

    return (
        <div className="x-vb-completed">
            <div className="dna-flex-apart">
                <div className=" x-inset-light orange x-vb-completed-msg">
                    Select a mission from the vault to open it and temporarily add it to your PM WORK SPACE.
                </div>

                <div className="dna-center-text">
                    🕸️🦇 &nbsp;
                    <input
                        className="w-200"
                        type="text"
                        placeHolder="Quick find..."
                        onChange={(e) => {
                            gridRef.current.api.setQuickFilter(e.target.value)
                        }}
                    />
                </div>
            </div>
            <div className={cx("ag-theme-alpine dna-ag-grid x-vb-completed-grid shadow")}>
                <AgGridReact
                    ref={gridRef}
                    getRowId={getRowId}
                    groupDisplayType="singleColumn"
                    groupMaintainOrder={true}
                    defaultColDef={defaultColDef}
                    rowData={completedMissions}
                    columnDefs={columnDefs}
                    animateRows={true}
                    onCellClicked={(p) => {
                        addParam("mission", p.data._id)
                    }}
                    suppressContextMenu={true}
                    includeHiddenColumnsInQuickFilter={true}
                ></AgGridReact>
            </div>
        </div>
    )
}

export default Board
